import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../api/contentStudioV2';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const ProductionMode = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Video submission state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoNotes, setVideoNotes] = useState('');
  const [videoSubmitting, setVideoSubmitting] = useState(false);

  useEffect(() => {
    loadApprovedSubmissions();
  }, []);

  const loadApprovedSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.getMySubmissions(token, user?.user_id);
      // Filter only approved submissions that haven't completed video submission
      const approvedSubmissions = (response.submissions || []).filter(
        s => s.submission_status === 'approved'
      );
      setSubmissions(approvedSubmissions);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenVideoModal = (submission) => {
    setSelectedSubmission(submission);
    setVideoUrl(submission.video_url || '');
    setVideoNotes(submission.video_notes || '');
    setShowVideoModal(true);
  };

  const handleSubmitVideo = async () => {
    if (!videoUrl.trim()) {
      alert('Please enter a video URL');
      return;
    }
    
    if (!videoUrl.startsWith('http://') && !videoUrl.startsWith('https://')) {
      alert('Please enter a valid URL starting with http:// or https://');
      return;
    }

    try {
      setVideoSubmitting(true);
      await api.submitVideo(token, selectedSubmission.session_id, videoUrl, videoNotes);
      alert('✅ Video submitted for review!');
      setShowVideoModal(false);
      setVideoUrl('');
      setVideoNotes('');
      loadApprovedSubmissions();
    } catch (error) {
      console.error('Failed to submit video:', error);
      alert('Failed to submit video: ' + error.message);
    } finally {
      setVideoSubmitting(false);
    }
  };

  const getVideoStatusBadge = (status) => {
    switch (status) {
      case 'pending_review':
        return <Badge className="bg-purple-500 text-white">🎬 Video Pending Review</Badge>;
      case 'approved':
        return <Badge className="bg-green-500 text-white">✅ Production Complete</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white">❌ Video Rejected</Badge>;
      default:
        return <Badge className="bg-green-500 text-white">📹 Ready for Shooting</Badge>;
    }
  };

  // Separate into Ready for Shooting and Video Submitted
  const readyForShooting = submissions.filter(s => !s.video_status);
  const videoSubmitted = submissions.filter(s => s.video_status);

  return (
    <div className="p-4 pb-24 space-y-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">🎬 Production Mode</h1>
          <p className="text-sm text-gray-500">Your approved storyboards ready for production</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate('/dashboard')}
          className="text-sm"
        >
          ← Back
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-3 border-emzi-blue border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : submissions.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="py-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-bold text-gray-800">No Approved Storyboards</h3>
            <p className="text-gray-600 mt-2">Wait for your supervisor to approve your storyboards.</p>
            <Button onClick={() => navigate('/my-submissions')} className="mt-4 bg-emzi-blue">
              View All Submissions
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Ready for Shooting Section */}
          {readyForShooting.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-bold text-green-600 flex items-center gap-2">
                📹 Ready for Shooting
                <Badge className="bg-green-100 text-green-700">{readyForShooting.length}</Badge>
              </h2>
              
              {readyForShooting.map((sub) => (
                <Card key={sub.session_id} className="border-0 shadow-lg bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-bold text-gray-800">{sub.submission_id}</span>
                          {getVideoStatusBadge(sub.video_status)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {sub.objective_type} • {sub.format_name || 'Content'}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          📅 Approved: {sub.decided_at ? new Date(sub.decided_at).toLocaleDateString() : 'N/A'}
                        </div>
                        {sub.supervisor_comment && (
                          <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            💬 {sub.supervisor_comment}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600 text-white"
                          onClick={() => handleOpenVideoModal(sub)}
                        >
                          📹 Submit Video
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/my-submissions`)}
                        >
                          View Storyboard
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Video Submitted Section */}
          {videoSubmitted.length > 0 && (
            <div className="space-y-3 mt-6">
              <h2 className="text-lg font-bold text-purple-600 flex items-center gap-2">
                🎬 Video Submitted
                <Badge className="bg-purple-100 text-purple-700">{videoSubmitted.length}</Badge>
              </h2>
              
              {videoSubmitted.map((sub) => (
                <Card 
                  key={sub.session_id} 
                  className={`border-0 shadow-lg ${
                    sub.video_status === 'approved' ? 'bg-gradient-to-r from-green-50 to-emerald-50' :
                    sub.video_status === 'rejected' ? 'bg-gradient-to-r from-red-50 to-orange-50' :
                    'bg-gradient-to-r from-purple-50 to-indigo-50'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-bold text-gray-800">{sub.submission_id}</span>
                          {getVideoStatusBadge(sub.video_status)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {sub.objective_type} • {sub.format_name || 'Content'}
                        </div>
                        {sub.video_url && (
                          <div className="mt-2 text-xs">
                            🔗 <a href={sub.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                              View Video
                            </a>
                          </div>
                        )}
                        {sub.video_status === 'rejected' && sub.video_rejection_reason && (
                          <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                            ❌ Rejection: {sub.video_rejection_reason}
                          </div>
                        )}
                      </div>
                      {sub.video_status === 'rejected' && (
                        <Button 
                          size="sm" 
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          onClick={() => handleOpenVideoModal(sub)}
                        >
                          🔄 Re-submit
                        </Button>
                      )}
                      {sub.video_status === 'approved' && (
                        <div className="text-4xl">🎉</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Video Submission Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="text-lg">📹 Submit Final Video</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
                <p className="font-medium">🎬 {selectedSubmission?.submission_id}</p>
                <p className="mt-1">Submit your final video link for supervisor review.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL *
                </label>
                <Input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=... or https://t.me/..."
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  YouTube, TikTok, Telegram, Vimeo, or any public link
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <Textarea
                  value={videoNotes}
                  onChange={(e) => setVideoNotes(e.target.value)}
                  placeholder="Any notes for the supervisor..."
                  rows={3}
                  className="w-full"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowVideoModal(false);
                    setVideoUrl('');
                    setVideoNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={handleSubmitVideo}
                  disabled={videoSubmitting || !videoUrl.trim()}
                >
                  {videoSubmitting ? '⏳ Submitting...' : '📤 Submit Video'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ProductionMode;
